import React, { useState, useEffect, useCallback } from "react";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import {
  Calendar,
  Clipboard,
  File,
  Folder,
  Search,
  Hourglass,
  TriangleAlert,
  SquareCheckBig,
  Inbox,
  Lightbulb,
  Eye,
  Upload,
  DownloadIcon,
} from "lucide-react";
import { useNotifications } from "@/hooks/useNotifications";

const DocumentUploadArea = ({
  tramiteRequestId,
  onDocumentsChange,
  onAllApproved,
}) => {
  const { success, error } = useNotifications();

  const [documentsStatus, setDocumentsStatus] = useState(null);
  const [uploading, setUploading] = useState({});
  const [loading, setLoading] = useState(true);
  const [draggedDoc, setDraggedDoc] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);

  useEffect(() => {
    if (tramiteRequestId) {
      fetchDocumentsStatus();
      // Polling cada 30 segundos para actualizaciones de estado
      const interval = setInterval(fetchDocumentsStatus, 30000);
      return () => clearInterval(interval);
    }
  }, [tramiteRequestId]);

  useEffect(() => {
    if (documentsStatus && onDocumentsChange) {
      onDocumentsChange(documentsStatus);

      // Verificar si todos están aprobados
      if (documentsStatus.all_approved && onAllApproved) {
        onAllApproved();
      }
    }
  }, [documentsStatus, onDocumentsChange, onAllApproved]);

  const fetchDocumentsStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/documents/${tramiteRequestId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setDocumentsStatus(data);
      } else {
        console.error("Error fetching documents status");
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (requiredDocName, file) => {
    if (!file) return;

    // Validaciones
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      success("El archivo es demasiado grande. Máximo 10MB permitido.");
      return;
    }

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];
    if (!allowedTypes.includes(file.type)) {
      success("Tipo de archivo no permitido. Solo se permiten PDF, JPG, PNG.");
      return;
    }

    setUploading((prev) => ({ ...prev, [requiredDocName]: true }));

    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/documents/${tramiteRequestId}?document_name=${encodeURIComponent(requiredDocName)}&notes=${encodeURIComponent("")}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        },
      );

      if (response.ok) {
        await fetchDocumentsStatus();
        success(
          <SquareCheckBig /> +
            `${requiredDocName} subido exitosamente. Nuestro equipo lo revisará pronto.`,
        );
      } else {
        const errorData = await response.json();
        success(`Error subiendo documento: ${errorData.detail}`);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      success("Error subiendo el documento. Intenta de nuevo.");
    } finally {
      setUploading((prev) => ({ ...prev, [requiredDocName]: false }));
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  const handleDragEnter = useCallback((e, docName) => {
    e.preventDefault();
    setDraggedDoc(docName);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDraggedDoc(null);
  }, []);

  const handleDrop = useCallback(
    (e, docName) => {
      e.preventDefault();
      setDraggedDoc(null);

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleFileUpload(docName, files[0]);
      }
    },
    [tramiteRequestId],
  );

  const getStatusInfo = (status) => {
    switch (status) {
      case "approved":
        return {
          icon: <SquareCheckBig />,
          color: "green",
          bg: "bg-green-50",
          border: "border-green-200",
          text: "text-green-800",
        };
      case "rejected":
        return {
          icon: "❌",
          color: "red",
          bg: "bg-red-50",
          border: "border-red-200",
          text: "text-red-800",
        };
      case "in_review":
        return {
          icon: "👀",
          color: "blue",
          bg: "bg-blue-50",
          border: "border-blue-200",
          text: "text-blue-800",
        };
      case "pending":
        return {
          icon: <Hourglass />,
          color: "yellow",
          bg: "bg-yellow-50",
          border: "border-yellow-200",
          text: "text-yellow-800",
        };
      default:
        return {
          icon: "📄",
          color: "gray",
          bg: "bg-gray-50",
          border: "border-gray-200",
          text: "text-gray-800",
        };
    }
  };

  const getStatusMessage = (docStatus) => {
    if (!docStatus.uploaded) return "Requerido - Arrastra tu archivo aquí";

    switch (docStatus.document?.status) {
      case "approved":
        return "Aprobado - Documento validado correctamente";
      case "rejected":
        return "Rechazado - Revisar comentarios y resubir";
      case "in_review":
        return "En revisión - Nuestro equipo está verificando";
      case "pending":
        return "Pendiente - Esperando revisión";
      default:
        return "Estado desconocido";
    }
  };

  const openPreview = (doc) => {
    setPreviewDoc(doc);
  };

  if (loading) {
    return <LoadingSpinner message="Cargando área de documentos..." />;
  }

  if (!documentsStatus) {
    return (
      <div className="text-center py-8">
        <div className="text-4xl mb-4">📄</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Error cargando documentos
        </h3>
        <p className="text-gray-500">
          No se pudo cargar la información de documentos requeridos
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-8 shadow-lg ">
      <h1>Documentos</h1>

      {/* Stats rápidas
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Total"
          value={documentsStatus.total_required}
          icon={<Clipboard />}
          color="gray"
        />
        <StatCard
          title="Subidos"
          value={documentsStatus.total_uploaded}
          icon={<Inbox />}
          color="blue"
        />
        <StatCard
          title="Aprobados"
          value={documentsStatus.total_approved}
          icon={<SquareCheckBig />}
          color="green"
        />
        <StatCard
          title="Pendientes"
          value={documentsStatus.total_required - documentsStatus.total_approved}
          icon={<Hourglass />}
          color="yellow"
        />
      </div> */}

      {/* Lista de documentos */}
      <div className="space-y-6 mt-4">
        {documentsStatus.documents_status?.map((docStatus, index) => {
          const statusInfo = getStatusInfo(docStatus.document?.status);
          const isUploading = uploading[docStatus.required_name];
          const isDragged = draggedDoc === docStatus.required_name;

          return (
            <div
              key={index}
              className={`relative border-2 border-dashed rounded-xl p-6 transition-all duration-300 ${
                isDragged
                  ? "border-blue-400 bg-blue-50 scale-105"
                  : docStatus.uploaded
                    ? `${statusInfo.border} ${statusInfo.bg}`
                    : "border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100"
              }`}
              onDragOver={handleDragOver}
              onDragEnter={(e) => handleDragEnter(e, docStatus.required_name)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, docStatus.required_name)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <span className="text-3xl">
                      {docStatus.uploaded ? statusInfo.icon : <File />}
                    </span>
                    <div>
                      <h3 className="font-bold text-lg text-gray-900">
                        {docStatus.required_name}
                      </h3>
                      <p className={`text-sm font-medium ${statusInfo.text}`}>
                        {getStatusMessage(docStatus)}
                      </p>
                    </div>
                  </div>

                  {/* Información del archivo subido */}
                  {docStatus.uploaded && docStatus.document && (
                    <div className="ml-12 space-y-2">
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span className="flex row items-center">
                          <Folder /> {docStatus.document._id}
                        </span>
                        <span className="flex row items-center">
                          <File />{" "}
                          {(docStatus.document.file_size / 1024 / 1024).toFixed(
                            2,
                          )}{" "}
                          MB
                        </span>
                        <span className="flex row items-center">
                          <Calendar />{" "}
                          {new Date(
                            docStatus.document.updated_at,
                          ).toLocaleDateString()}
                        </span>
                      </div>

                      {/* Botón de preview */}
                      <button
                        onClick={() => openPreview(docStatus.document)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Ver documento
                      </button>

                      {/* Notas de revisión */}
                      {docStatus.document.current_review_notes && (
                        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm text-blue-800">
                            <strong>
                              <MessageCircle /> Comentarios del revisor:
                            </strong>{" "}
                            {docStatus.document.current_review_notes}
                          </p>
                        </div>
                      )}

                      {/* Motivo de rechazo */}
                      {docStatus.document.rejection_reason && (
                        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm text-red-800">
                            <strong>❌ Motivo de rechazo:</strong>{" "}
                            {docStatus.document.rejection_reason}
                          </p>
                          {docStatus.document.requires_resubmission && (
                            <p className="text-xs text-red-600 mt-1">
                              <TriangleAlert /> Se requiere volver a subir este
                              documento corregido
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Área de drag & drop para documentos faltantes */}
                  {!docStatus.uploaded && (
                    <div className="ml-12 mt-4">
                      <div className="text-sm text-gray-500 space-y-1">
                        <p className="flex row items-center">
                          <Inbox className="mr-1" /> Arrastra tu archivo aquí o
                          haz clic para seleccionar
                        </p>
                        <p className="flex row items-center">
                          <Clipboard className="mr-1" /> Formatos: PDF, JPG, PNG
                          • Máximo: 10MB
                        </p>
                        <p className="flex row items-center">
                          <Lightbulb className="mr-1" /> Puedes subir múltiples
                          versiones si es necesario
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Botones de acción */}
                <div className="ml-4 flex flex-col space-y-2">
                  {/* Botón de upload */}
                  {isUploading ? (
                    <div className="flex items-center text-blue-600 bg-blue-50 px-4 py-2 rounded-lg">
                      <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full mr-2"></div>
                      Subiendo...
                    </div>
                  ) : (
                    <label className="cursor-pointer bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium text-center flex row items-center">
                      <Upload className="mr-1" />{" "}
                      {docStatus.uploaded ? "Reemplazar" : "Subir Archivo"}
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            handleFileUpload(docStatus.required_name, file);
                          }
                        }}
                      />
                    </label>
                  )}

                  {/* Botón de preview */}
                  {docStatus.uploaded && (
                    <button
                      onClick={() => openPreview(docStatus.document)}
                      className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium flex row items-center"
                    >
                      <Eye className="mr-1" /> Preview
                    </button>
                  )}
                </div>
              </div>

              {/* Estado de revisión prominente */}
              {docStatus.uploaded && (
                <div
                  className={`mt-4 p-4 rounded-lg ${statusInfo.bg} ${statusInfo.border} border`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{statusInfo.icon}</span>
                      <div>
                        <p className={`font-semibold ${statusInfo.text}`}>
                          {docStatus.document.status === "approved" &&
                            "Documento Aprobado"}
                          {docStatus.document.status === "rejected" &&
                            "Documento Rechazado"}
                          {docStatus.document.status === "in_review" &&
                            "En Revisión"}
                          {docStatus.document.status === "pending" &&
                            "Pendiente de Revisión"}
                        </p>
                        <p className={`text-sm ${statusInfo.text}`}>
                          {docStatus.document.reviewed_at &&
                            `Revisado el ${new Date(docStatus.document.reviewed_at).toLocaleDateString()}`}
                        </p>
                      </div>
                    </div>

                    {/* Indicador de prioridad */}
                    {docStatus.document.priority_level !== "normal" && (
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          docStatus.document.priority_level === "urgent"
                            ? "bg-red-100 text-red-800"
                            : "bg-orange-100 text-orange-800"
                        }`}
                      >
                        {docStatus.document.priority_level === "urgent"
                          ? "🚨 Urgente"
                          : "<Zap /> Alta"}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Mensaje de estado global */}
      {documentsStatus.all_approved && (
        <div className="mt-8 bg-green-50 border-2 border-green-300 rounded-xl p-6">
          <div className="flex items-center justify-center">
            <span className="text-4xl mr-4">🎊</span>
            <div className="text-center">
              <h3 className="text-xl font-bold text-green-800 mb-2">
                ¡Documentación Completa y Aprobada!
              </h3>
              <p className="text-green-700">
                Todos tus documentos han sido validados. Tu trámite está listo
                para cotización.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Modal de preview */}
      {previewDoc && (
        <DocumentPreviewModal
          document={previewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </div>
  );
};

const StatCard = ({ title, value, icon, color }) => {
  const colorClasses = {
    gray: "bg-gray-50 text-gray-600 border-gray-200",
    blue: "bg-blue-50 text-blue-600 border-blue-200",
    green: "bg-green-50 text-green-600 border-green-200",
    yellow: "bg-yellow-50 text-yellow-600 border-yellow-200",
  };

  return (
    <div className={`p-4 rounded-lg border ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium opacity-75">{title}</p>
          <p className="text-xl font-bold">{value}</p>
        </div>
        <div className="text-2xl">{icon}</div>
      </div>
    </div>
  );
};

const DocumentPreviewModal = ({ document, onClose }) => {
  const isImage = document.file_path.match(/\.(jpg|jpeg|png|webp)$/i);
  const isPDF = document.file_path.match(/\.pdf$/i);

  const handleDownload = async (document) => {
    const token = localStorage.getItem("token");
    await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/documents/${document._id}/download`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    )
      .then(async (res) => {
        if (!res.ok) throw new Error("No autorizado o archivo no encontrado");

        const blob = await res.blob();
        const fileURL = URL.createObjectURL(blob);
        window.open(fileURL, "_blank");
      })
      .catch((err) => console.error(err));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full mx-4 overflow-auto max-h-[570px]">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-gray-900">
              Preview: {document.required_document_name}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="text-center space-y-4">
            <h4 className="text-lg font-medium text-gray-900">
              {document.current_filename}
            </h4>
            <div className="flex items-center justify-center space-x-4 text-sm text-gray-600">
              <span className="flex row items-center">
                <File className="mr-1" />{" "}
                {(document.file_size / 1024 / 1024).toFixed(2)} MB
              </span>
              <span className="flex row items-center">
                <Calendar className="mr-1" />{" "}
                {new Date(document.updated_at).toLocaleDateString()}
              </span>
              <span
                className={`px-2 py-1 rounded text-xs font-medium ${
                  document.status === "approved"
                    ? "bg-green-100 text-green-800"
                    : document.status === "rejected"
                      ? "bg-red-100 text-red-800"
                      : document.status === "in_review"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-yellow-100 text-yellow-800"
                }`}
              >
                {document.status}
              </span>
              <button
                onClick={() => handleDownload(document)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                <DownloadIcon />
              </button>
            </div>

            {/* Preview placeholder */}
            <div className="bg-gray-100 rounded-lg p-8 text-gray-500 text-center">
              <div className="flex justify-center">
                {isImage && (
                  <img
                    src={
                      process.env.REACT_APP_BACKEND_URL +
                      "/uploads/" +
                      document.file_path
                    }
                    className="max-w-sm rounded shadow"
                  />
                )}

                {isPDF && (
                  <iframe
                    src={
                      process.env.REACT_APP_BACKEND_URL +
                      "/uploads/" +
                      document.file_path
                    }
                    className="w-full h-[600px] border rounded"
                  ></iframe>
                )}

                {!isImage && !isPDF && (
                  <p className="text-red-500">
                    Tipo de archivo no soportado para preview.
                  </p>
                )}
              </div>
            </div>

            {/* Historial de revisión */}
            {document.review_history && document.review_history.length > 0 && (
              <div className="mt-6 text-left">
                <h4 className="font-medium text-gray-900 mb-3">
                  <Clipboard /> Historial de Revisión
                </h4>
                <div className="space-y-2">
                  {document.review_history.map((review, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-gray-50 rounded-lg text-sm"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">
                          {review.reviewer_name}
                        </span>
                        <span className="text-gray-500">
                          {new Date(review.reviewed_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-gray-700">
                        Estado: <strong>{review.status}</strong>
                        {review.review_notes && ` • ${review.review_notes}`}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadArea;
